import { defineStore } from "pinia";

import router from "@/router";

interface AuthState {
  token: string;
  login: string;
}

export const useAuthStore = defineStore({
  id: "auth",
  state: (): {
    token: string;
    refreshToken: string;
    user: object | string;
    auth: AuthState | null;
    expiresAt: number | null;
    passwordSuggestions: string[];
    billingStatus: boolean | null;
    hasRefreshed: boolean;
  } => ({
    token: "",
    refreshToken: "",
    user: {},
    auth: null,
    expiresAt: null,
    passwordSuggestions: [],
    billingStatus: null,
    hasRefreshed: false,
  }),

  getters: {
    getAutoSign(state): AuthState | null {
      const auth = localStorage.getItem("auth");
      if (!auth) return null;
      return JSON.parse(auth) ?? state.auth;
    },

    getToken(state): string {
      return state.token || localStorage.getItem("token") || "";
    },

    getRefreshToken(state): string {
      return state.refreshToken || localStorage.getItem("refreshToken") || "";
    },

    getHasRefreshed(state): boolean {
      const stored = localStorage.getItem("hasRefreshed");
      return stored === "true" || state.hasRefreshed;
    },

    getUser(state): object | string {
      const user = localStorage.getItem("user");
      if (!user) return "";

      return JSON.parse(localStorage.getItem("user") ?? "") ?? state.user;
    },

    getBillingStatus(state): boolean | null {
      const stored = localStorage.getItem("billingStatus");
      if (stored == null) return state.billingStatus;
      try {
        return JSON.parse(stored);
      } catch (_) {
        return stored === "true";
      }
    },

    getExpiresAt(): number | null {
      return (
        this.expiresAt || parseInt(localStorage.getItem("expiresAt") ?? "0")
      );
    },

    getPasswordSuggestions(state): string[] {
      return state.passwordSuggestions;
    },
  },

  actions: {
    setAutoSign(data: AuthState) {
      localStorage.setItem("auth", JSON.stringify(data));
      this.auth = data;
    },

    clearAutoSign() {
      localStorage.removeItem("auth");
      this.auth = null;
      this.token = "";
    },

    setToken(token: string) {
      localStorage.setItem("token", token);
      this.token = token;
    },

    setRefreshToken(refreshToken: string) {
      localStorage.setItem("refreshToken", refreshToken);
      this.refreshToken = refreshToken;
    },

    setHasRefreshed(hasRefreshed: boolean) {
      localStorage.setItem("hasRefreshed", hasRefreshed.toString());
      this.hasRefreshed = hasRefreshed;
    },

    setUser(user: object) {
      localStorage.setItem("user", JSON.stringify(user));
      this.user = user;
    },

    setExpiresAt(expiresAt: number) {
      localStorage.setItem("expiresAt", expiresAt.toString());
      this.expiresAt = expiresAt;
    },

    setPasswordSuggestions(suggestions: string[]) {
      this.passwordSuggestions = suggestions
    },

    setBillingStatus(status: boolean | null | undefined) {
      if (typeof status === "undefined") return;
      localStorage.setItem("billingStatus", JSON.stringify(!!status));
      this.billingStatus = !!status;
    },

    setLogout() {
      localStorage.removeItem("auth");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("expiresAt");
      localStorage.removeItem("billingStatus");
      localStorage.removeItem("hasRefreshed");

      this.auth = null;
      this.token = "";
      this.refreshToken = "";
      this.user = {};
      this.expiresAt = null;
      this.billingStatus = null;
      this.hasRefreshed = false;

      setTimeout(() => {
        router.push({ name: "SignInPage" }).then(() => {
          router.go(0);
        });
      }, 2000);
    },
  },
});
